import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Divider,
  HStack,
  Text,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Portal,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';

type stringFunction = () => string;
export interface MultiSelectProps<T extends string = string> {
  onChange: (nextValue: T, isOpen: boolean) => void;
  onMenuClosed: () => void;
  onApplyOptions?: (selectedOptions?: T[]) => void;
  options: { label: string | React.Component; value: string }[];
  selectedOptions: T[];
  optionsHeader?: string;
  applyButtonText?: string;
  titleText?: string | stringFunction;
  fullWidth?: boolean;
}

export interface NewMultiSelectProps<T extends string = string> {
  onApplyOptions: (selectedOptions: T[]) => void;
  options: { label: string | React.Component; value: T }[];
  initialSelections: T[];
  optionsHeader?: string;
  applyButtonText?: string;
  titleText?: string | stringFunction;
  fullWidth?: boolean;
  width?: string;
  zIndex?: number;
  colorScheme?: string;
  borderWidth?: string;
  iconButton?: React.ReactElement;
  menuTopMargin?: string;
}

export function MultiSelect<T extends string = string>({
  onChange,
  onMenuClosed = () => {},
  onApplyOptions,
  options,
  selectedOptions,
  optionsHeader = '',
  applyButtonText = '',
  titleText = '',
  fullWidth = true,
}: MultiSelectProps<T>) {
  return (
    <Box w={fullWidth ? '100%' : 'auto'}>
      <Menu
        matchWidth
        autoSelect={false}
        eventListeners={{ scroll: false, resize: true }}
        onClose={() => onMenuClosed()}
      >
        {({ onClose, isOpen }) => (
          <>
            <MenuButton
              as={Button}
              variant="outline"
              minWidth="250px"
              minHeight="40px"
              w={fullWidth ? '100%' : 'auto'}
            >
              <HStack justifyContent="space-between">
                <Text style={{ fontWeight: 400 }}>{titleText}</Text>
                <ChevronDownIcon />
              </HStack>
            </MenuButton>
            <MenuList>
              <MenuOptionGroup
                value={selectedOptions}
                title={optionsHeader}
                type="checkbox"
              >
                {options.map(({ label, value }) => {
                  return (
                    <MenuItemOption
                      key={value}
                      value={value}
                      onClick={() => onChange(value as T, isOpen)}
                      closeOnSelect={false}
                    >
                      {label}
                    </MenuItemOption>
                  );
                })}
              </MenuOptionGroup>

              {applyButtonText && (
                <>
                  <Divider width="90%" margin="5px auto" />
                  <Box align="center" mb="5px">
                    <Button
                      h="1.75rem"
                      size="sm"
                      w="90%"
                      mt="10px"
                      variant="outline"
                      fontWeight="normal"
                      onClick={() => {
                        if (onApplyOptions) onApplyOptions(selectedOptions);
                        onClose();
                      }}
                    >
                      {applyButtonText}
                    </Button>
                  </Box>
                </>
              )}
            </MenuList>
          </>
        )}
      </Menu>
    </Box>
  );
}

export function IsolatedMultiSelect<T extends string = string>({
  onApplyOptions,
  initialSelections,
  options,
  optionsHeader = '',
  applyButtonText = '',
  titleText = '',
  width,
  fullWidth = true,
  zIndex,
  colorScheme,
  borderWidth,
  iconButton,
  menuTopMargin,
}: NewMultiSelectProps<T>) {
  const [selectedOptions, setSelectedOptions] = useState<T[]>(
    initialSelections,
  );

  // if initial selections changes, we want to update the selected options
  useEffect(() => {
    setSelectedOptions(initialSelections);
  }, [initialSelections]);

  const onOptionChangeHandler = (value: T) => {
    const updatedOptions = [...selectedOptions];
    const index = updatedOptions.indexOf(value);
    if (index >= 0) {
      updatedOptions.splice(index, 1);
    } else {
      updatedOptions.push(value);
    }
    setSelectedOptions(updatedOptions);
  };
  const getWidth = () => {
    if (width) {
      return width;
    }
    if (fullWidth) {
      return '100%';
    }
    return 'auto';
  };

  return (
    <Menu
      matchWidth
      autoSelect={false}
      eventListeners={{ scroll: false, resize: true }}
    >
      {({ onClose }) => (
        <>
          {iconButton ? (
            <MenuButton>{iconButton}</MenuButton>
          ) : (
            <MenuButton
              as={Button}
              variant="outline"
              minWidth="250px"
              minHeight="40px"
              w={getWidth()}
              colorScheme={colorScheme}
              borderWidth={borderWidth}
            >
              <HStack justifyContent="space-between">
                <Text style={{ fontWeight: 400 }}>{titleText}</Text>
                <ChevronDownIcon />
              </HStack>
            </MenuButton>
          )}
          <MenuList
            maxH="250"
            overflowY="scroll"
            zIndex={zIndex || 'dropdown'}
            mt={menuTopMargin}
          >
            <MenuOptionGroup
              value={selectedOptions}
              title={optionsHeader}
              type="checkbox"
            >
              {options.map(({ label, value }) => {
                return (
                  <MenuItemOption
                    key={value}
                    value={value}
                    onClick={() => onOptionChangeHandler(value as T)}
                    closeOnSelect={false}
                  >
                    {label}
                  </MenuItemOption>
                );
              })}
            </MenuOptionGroup>

            {applyButtonText && !_.isEqual(selectedOptions, initialSelections) && (
              <>
                <Divider width="90%" margin="5px auto" />
                <Box align="center" mb="5px">
                  <Button
                    h="1.75rem"
                    size="sm"
                    w="90%"
                    mt="10px"
                    variant="outline"
                    fontWeight="normal"
                    onClick={() => {
                      onApplyOptions(selectedOptions);
                      onClose();
                    }}
                  >
                    {applyButtonText}
                  </Button>
                </Box>
              </>
            )}
          </MenuList>
        </>
      )}
    </Menu>
  );
}

export function IsolatedMultiSelectWithPortalIconButton<
  T extends string = string
>({
  onApplyOptions,
  initialSelections,
  options,
  optionsHeader = '',
  applyButtonText = '',
  icon,
  zIndex,
}: NewMultiSelectProps<T> & { icon: React.ReactElement }) {
  const [selectedOptions, setSelectedOptions] = useState<T[]>(
    initialSelections,
  );

  // if initial selections changes, we want to update the selected options
  useEffect(() => {
    setSelectedOptions(initialSelections);
  }, [initialSelections]);

  const onOptionChangeHandler = (value: T) => {
    const updatedOptions = [...selectedOptions];
    const index = updatedOptions.indexOf(value);
    if (index >= 0) {
      updatedOptions.splice(index, 1);
    } else {
      updatedOptions.push(value);
    }
    setSelectedOptions(updatedOptions);
  };

  return (
    <Menu
      matchWidth
      autoSelect={false}
      eventListeners={{ scroll: false, resize: true }}
    >
      {({ onClose }) => (
        <>
          <MenuButton>{icon}</MenuButton>
          <Portal>
            <MenuList maxH="250" maxW="100" overflowY="scroll" zIndex={zIndex}>
              <MenuOptionGroup
                value={selectedOptions}
                title={optionsHeader}
                type="checkbox"
              >
                {options.map(({ label, value }) => {
                  return (
                    <MenuItemOption
                      key={value}
                      value={value}
                      onClick={() => onOptionChangeHandler(value as T)}
                      closeOnSelect={false}
                      fontSize="12px"
                    >
                      {label}
                    </MenuItemOption>
                  );
                })}
              </MenuOptionGroup>

              {applyButtonText &&
                !_.isEqual(selectedOptions, initialSelections) && (
                  <>
                    <Divider width="90%" margin="5px auto" />
                    <Box align="center" mb="5px">
                      <Button
                        h="1.75rem"
                        size="sm"
                        w="90%"
                        mt="10px"
                        variant="outline"
                        fontWeight="normal"
                        onClick={() => {
                          onApplyOptions(selectedOptions);
                          onClose();
                        }}
                      >
                        {applyButtonText}
                      </Button>
                    </Box>
                  </>
                )}
            </MenuList>
          </Portal>
        </>
      )}
    </Menu>
  );
}
